import React, { useState, useEffect } from 'react';
import { JobData, SortOption } from '../types';

interface JobListProps {
  jobs: JobData[];
  sortBy?: SortOption;
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (option: SortOption, direction: 'asc' | 'desc') => void;
  // New props for remote pagination
  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

const JobList: React.FC<JobListProps> = ({ 
  jobs, 
  sortBy, 
  sortDirection, 
  onSortChange,
  loading = false,
  hasMore = false,
  onLoadMore
}) => {
  // For display purposes, we'll show all jobs since they're already loaded from remote
  const [displayJobs, setDisplayJobs] = useState<JobData[]>([]);

  // Check if sorting is enabled
  const sortingEnabled = sortBy !== undefined && sortDirection !== undefined && onSortChange !== undefined;

  // Update display jobs when jobs change
  useEffect(() => {
    setDisplayJobs(jobs);
  }, [jobs]);

  // Handler for column header click
  const handleColumnHeaderClick = (column: SortOption) => {
    if (!sortingEnabled || !onSortChange || !sortBy) return;
    
    if (sortBy === column) {
      const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
      onSortChange(column, newDirection);
    } else {
      onSortChange(column, 'desc');
    }
  };

  // Render sort indicator
  const renderSortIndicator = (column: SortOption) => {
    if (!sortingEnabled || !sortBy) return null;
    
    if (sortBy === column) {
      return <span className="ml-2 text-[#4F46E5] font-bold text-base">{sortDirection === 'desc' ? '↓' : '↑'}</span>;
    }
    return <span className="ml-2 text-[#D1D5DB] text-sm group-hover:text-[#9CA3AF]">⇅</span>;
  };

  // Render load more button
  const renderLoadMoreButton = () => {
    if (!hasMore && !loading) return null;

    return (
      <div className="px-4 py-6 flex justify-center border-t border-[#E5E7EB] bg-[#F9FAFB] rounded-b-lg">
        {loading ? (
          <div className="flex items-center space-x-2 text-[#6B7280]">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#4F46E5]"></div>
            <span className="text-sm font-medium">Loading more jobs...</span>
          </div>
        ) : hasMore ? (
          <button
            onClick={onLoadMore}
            className="inline-flex items-center px-6 py-3 border border-[#4F46E5] text-sm font-medium rounded-md text-[#4F46E5] bg-white hover:bg-[#F5F3FF] hover:border-[#4338CA] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-2"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Load More Jobs
          </button>
        ) : null}
      </div>
    );
  };

  // Render mobile job card
  const renderMobileJobCard = (job: JobData) => (
    <div key={job.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-4 shadow-sm hover:shadow-md transition-shadow duration-150">
      <div className="space-y-3">
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-[#1F2937] break-words leading-tight">{job.jobTitle}</h3>
            <p className="text-sm text-[#6B7280] truncate">{job.companyName}</p>
          </div>
          <div className="text-right ml-4 flex-shrink-0">
            <div className="text-sm font-medium text-[#4F46E5]">${Math.round(job.baseSalary).toLocaleString()}</div>
          </div>
        </div>
        
        <div className="flex items-center justify-between text-xs text-[#6B7280]">
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="truncate">{job.location}</span>
          </div>
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{new Date(job.decisionDate).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-light text-[#1F2937]">
            <span className="font-semibold">{jobs.length}</span> Jobs Found
            {hasMore && (
              <span className="text-sm text-[#6B7280] ml-2 font-normal">
                (More available)
              </span>
            )}
          </h2>
          {jobs.length > 0 && sortingEnabled && (
            <p className="text-sm text-[#6B7280] mt-1">
              <span className="inline-flex items-center">
                Click any column header to sort results
              </span>
            </p>
          )}
        </div>
      </div>

      {jobs.length > 0 ? (
        <>
          {/* Mobile view - Card layout */}
          <div className="block md:hidden">
            <div className="space-y-2">
              {displayJobs.map(renderMobileJobCard)}
            </div>
            {renderLoadMoreButton()}
          </div>

          {/* Desktop view - Table layout */}
          <div className="hidden md:block">
            <div className="overflow-hidden bg-white rounded-lg shadow-sm border border-[#E5E7EB]">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#E5E7EB]">
                  <thead className="bg-[#F9FAFB]">
                    <tr>
                      <th 
                        scope="col" 
                        className={`px-6 py-4 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider ${sortingEnabled ? 'cursor-pointer hover:bg-[#F3F4F6] transition-all duration-150 group relative' : ''}`}
                        onClick={sortingEnabled ? () => handleColumnHeaderClick('companyName') : undefined}
                        title={sortingEnabled ? "Click to sort by Company" : undefined}
                      >
                        <div className="flex items-center">
                          <span className={`${sortingEnabled && sortBy === 'companyName' ? 'text-[#4F46E5]' : ''} ${sortingEnabled ? 'group-hover:text-[#4B5563]' : ''}`}>Company</span>
                          {sortingEnabled && renderSortIndicator('companyName')}
                        </div>
                        {sortingEnabled && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#4F46E5] scale-x-0 group-hover:scale-x-100 transition-transform duration-150 origin-left"></div>}
                      </th>
                      <th 
                        scope="col" 
                        className={`px-6 py-4 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider ${sortingEnabled ? 'cursor-pointer hover:bg-[#F3F4F6] transition-all duration-150 group relative' : ''}`}
                        onClick={sortingEnabled ? () => handleColumnHeaderClick('jobTitle') : undefined}
                        title={sortingEnabled ? "Click to sort by Job Title" : undefined}
                      >
                        <div className="flex items-center">
                          <span className={`${sortingEnabled && sortBy === 'jobTitle' ? 'text-[#4F46E5]' : ''} ${sortingEnabled ? 'group-hover:text-[#4B5563]' : ''}`}>Job Title</span>
                          {sortingEnabled && renderSortIndicator('jobTitle')}
                        </div>
                        {sortingEnabled && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#4F46E5] scale-x-0 group-hover:scale-x-100 transition-transform duration-150 origin-left"></div>}
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                        Location
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-4 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider"
                      >
                        Decision Date
                      </th>
                      <th 
                        scope="col" 
                        className={`px-6 py-4 text-right text-xs font-medium text-[#6B7280] uppercase tracking-wider ${sortingEnabled ? 'cursor-pointer hover:bg-[#F3F4F6] transition-all duration-150 group relative' : ''}`}
                        onClick={sortingEnabled ? () => handleColumnHeaderClick('baseSalary') : undefined}
                        title={sortingEnabled ? "Click to sort by Salary" : undefined}
                      >
                        <div className="flex items-center justify-end">
                          <span className={`${sortingEnabled && sortBy === 'baseSalary' ? 'text-[#4F46E5]' : ''} ${sortingEnabled ? 'group-hover:text-[#4B5563]' : ''}`}>Salary</span>
                          {sortingEnabled && renderSortIndicator('baseSalary')}
                        </div>
                        {sortingEnabled && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#4F46E5] scale-x-0 group-hover:scale-x-100 transition-transform duration-150 origin-left"></div>}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#E5E7EB]">
                    {displayJobs.map((job) => (
                      <tr key={job.id} className="hover:bg-[#F9FAFB] transition-colors duration-150">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-[#1F2937] break-words max-w-[200px]">{job.companyName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-[#374151]]">{job.jobTitle}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-[#6B7280]">{job.location}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                          {new Date(job.decisionDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm font-medium text-[#4F46E5]">${Math.round(job.baseSalary).toLocaleString()}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderLoadMoreButton()}
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white p-8 rounded-lg shadow-sm border border-[#E5E7EB] text-center">
          <p className="text-[#6B7280] text-lg">No jobs found. Try adjusting your search criteria.</p>
        </div>
      )}
    </div>
  );
};

export default JobList; 